import { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Car } from 'lucide-react';
import { publicAPI, getMediaBySlugUrl } from '@/lib/api';
import CookieConsent, { setConsent } from '@/components/CookieConsent';

const Header = ({ content, media }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const logoMedia = media.find(m => m.slug === 'logo' && m.active);
  const partnerMedia = media.find(m => m.slug === 'partner_logo' && m.active);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? 'shadow-sm' : 'border-b border-slate-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <div className="flex items-center gap-5">
            <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo-link">
              {logoMedia ? (
                <img
                  src={getMediaBySlugUrl('logo')}
                  alt="GB Sylt"
                  className="h-8 md:h-10 w-auto"
                />
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-[#0E518D] rounded-sm flex items-center justify-center">
                    <Car className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="leading-none">
                    <span className="font-heading text-lg font-bold text-[#0E518D] block">
                      {content?.header_titel || 'GB Sylt'}
                    </span>
                    <span className="text-[10px] font-body text-slate-400 uppercase tracking-widest">
                      {content?.header_subtitel || 'Mietwagen Service'}
                    </span>
                  </div>
                </div>
              )}
            </Link>
            {partnerMedia && (
              <div className="hidden sm:flex items-center gap-2 pl-5 border-l border-slate-200">
                <img
                  src={getMediaBySlugUrl('partner_logo')}
                  alt="Partner"
                  className="h-6 md:h-7 w-auto opacity-60"
                />
              </div>
            )}
          </div>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/go" data-testid="header-cta-button">
              <Button className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-6 py-2.5 font-body font-medium tracking-wide text-xs uppercase transition-all duration-300">
                {content?.hero_button || 'Jetzt Mietwagen buchen'}
              </Button>
            </Link>
          </div>

          <button
            className="md:hidden p-2 -mr-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5 text-slate-600" /> : <Menu className="w-5 h-5 text-slate-600" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 animate-fade-in">
          <Link
            to="/go"
            className="block"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-cta-button"
          >
            <Button className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-6 py-3 font-body font-medium tracking-wide text-xs uppercase">
              {content?.hero_button || 'Jetzt Mietwagen buchen'}
            </Button>
          </Link>
        </div>
      )}
    </header>
  );
};

const Footer = ({ content, version }) => {
  return (
    <footer data-testid="main-footer" className="bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 bg-[#0E518D] rounded-sm flex items-center justify-center">
                <Car className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-heading text-lg font-semibold text-slate-900">
                {content?.header_titel || 'GB Sylt'}
              </span>
            </div>
            <p className="text-slate-500 text-sm font-body leading-relaxed">
              {content?.footer_text || 'Ein Service von GB Sylt'}
            </p>
          </div>
          <div>
            <h4 className="font-body font-semibold text-xs uppercase tracking-widest text-[#0E518D] mb-4">
              Rechtliches
            </h4>
            <div className="space-y-2.5">
              <Link
                to="/impressum"
                className="block text-slate-500 hover:text-[#0E518D] text-sm font-body transition-colors"
                data-testid="footer-impressum-link"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="block text-slate-500 hover:text-[#0E518D] text-sm font-body transition-colors"
                data-testid="footer-datenschutz-link"
              >
                Datenschutz
              </Link>
              <button
                onClick={() => {
                  setConsent(null);
                  localStorage.removeItem('cookie_consent');
                  window.location.reload();
                }}
                className="block text-slate-500 hover:text-[#0E518D] text-sm font-body transition-colors text-left"
                data-testid="footer-cookie-settings"
              >
                Cookie-Einstellungen
              </button>
            </div>
          </div>
          <div>
            <h4 className="font-body font-semibold text-xs uppercase tracking-widest text-[#0E518D] mb-4">
              Hinweis
            </h4>
            <p className="text-slate-400 text-xs font-body leading-relaxed">
              {content?.footer_affiliate_hinweis || 'Diese Seite enthält Affiliate-Links.'}
            </p>
            <p className="text-slate-300 text-xs font-body leading-relaxed mt-2">
              {content?.footer_markenhinweis || ''}
            </p>
          </div>
        </div>
        <div className="border-t border-slate-200 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-slate-400 text-xs font-body">
            {content?.kooperation_hinweis || 'Ein Service von GB Sylt in Kooperation mit unserem Mietwagenpartner.'}
          </p>
          <p className="text-slate-300 text-xs font-body" data-testid="footer-version">
            {version ? `Version ${version}` : ''}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function PublicLayout() {
  const [content, setContent] = useState({});
  const [media, setMedia] = useState([]);
  const [version, setVersion] = useState('');
  const [activeHero, setActiveHero] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [contentRes, mediaRes, versionRes, heroRes] = await Promise.all([
          publicAPI.getContent(),
          publicAPI.getMedia(),
          publicAPI.getVersion(),
          publicAPI.getActiveHero(),
        ]);
        setContent(contentRes.data);
        setMedia(mediaRes.data);
        setVersion(versionRes.data.version || '');
        setActiveHero(heroRes.data && heroRes.data.id ? heroRes.data : null);
      } catch (err) {
        console.error('Failed to load layout data:', err);
      }
    };
    load();
  }, []);

  return (
    <div className="public-layout min-h-screen flex flex-col bg-white">
      <Header content={content} media={media} />
      <main className="flex-grow pt-16 md:pt-[72px]">
        <Outlet context={{ content, media, activeHero }} />
      </main>
      <Footer content={content} version={version} />
      <CookieConsent />
    </div>
  );
}
